import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { UserService } from "@app2/account/user.service";
import { TicketWatcher } from "@app2/type-defs/ticket/ticket-types";
import { PeopleService } from "@app2/account/people.service";
import { PersonSummary } from "@app2/type-defs/user/user-types";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
    selector: "ticket-watchers-edit",
    template: `
        <ticket-watchers-list [editPerm]="editPerm"
                              [watchers]="watchers"
                              (onEditWatcherType)="onEditWatcherType($event)"
                              (onDeleteWatcher)="onDeleteWatcher($event)">
        </ticket-watchers-list>

        <ng-container *ngIf="isEditing">
            <ticket-watcher-select [(watcher)]="editWatcher"
                                   [currentWatchers]="watchers"
                                   [hideSelectorOnReset]="true"
                                   (clearWatcher)="isEditing = false">
            </ticket-watcher-select>
            <ds-button *ngIf="editWatcher?.type"
                       size="small"
                       class="link inline-add nice-form-display-text add-watcher"
                       test-id="save-watcher-button"
                       (click)="onWatcherAdded(editWatcher)">
                <i class="ion-plus"></i>
                {{ "ADD_WATCHER_WITH_NAME" | localize:getPersonName(editWatcher.personId) }}
            </ds-button>
        </ng-container>

        <div *ngIf="!isEditing">
            <ng-container *dsPermissionsRequired="editPerm">
                <div>
                    <button class="link inline-add nice-form-display-text"
                            test-id="add-watcher-button"
                            (click)="startAddingWatcher()">
                        <i class="ion-plus"></i>
                        {{ "ADD_WATCHERS" | localize }}
                    </button>
                </div>
                <div>
                    <button *ngIf="canAddSelfAsWatcher()"
                            (click)="onWatcherAdded({ personId: currentUserId, type: 'all' })"
                            class="link inline-add nice-form-display-text"
                            test-id="add-self-as-watcher-button">
                        <i class="ion-plus"></i>
                        {{ "ADD_SELF_WATCHER" | localize }}
                    </button>
                </div>
            </ng-container>
        </div>
    `,
    styles: [`
        .add-watcher {
            bottom: 10px;
        }
    `],
})
export class TicketWatchersEditComponent implements OnInit {
    @Input() editPerm: string;
    @Input() watchers: TicketWatcher[] = [];

    @Output() watchersChange = new EventEmitter<TicketWatcher[]>();

    isEditing: boolean;
    currentUserId: uuid;
    // Used when editing a watcher that is already added
    editWatcher: TicketWatcher = null;

    private people: PersonSummary[] = [];

    constructor(private userService: UserService,
                private peopleService: PeopleService) {
        this.currentUserId = userService.getCurrentUser().id;
    }

    ngOnInit() {
        this.peopleService.getActiveUsersWithAccessToCurrentOrg$(false)
            .pipe(untilDestroyed(this))
            .subscribe(users => users.map(user => this.people[user.id] = user.fullName));
    }

    canAddSelfAsWatcher() {
        return !this.watchers.map(w => w.personId).includes(this.userService.getCurrentUser().id);
    }

    onWatcherAdded(newWatcher: TicketWatcher) {
        const existingWatcher = this.watchers.find(w => w.personId === newWatcher.personId);

        // If we are adding a new watcher
        if (!existingWatcher) {
            this.watchersChange.emit([...this.watchers, newWatcher]);
        } else if (existingWatcher.type !== newWatcher.type) {
            // If we are editing a watcher that is already in the watchers list
            existingWatcher.type = newWatcher.type;
            this.watchersChange.emit([...this.watchers]);
        }
        this.isEditing = false;
    }

    onDeleteWatcher(watcherId: uuid) {
        this.watchersChange.emit(this.watchers.filter(w => w.personId !== watcherId));
    }

    startAddingWatcher() {
        this.isEditing = true;
        this.editWatcher = null;
    }

    onEditWatcherType(selectedWatcher: TicketWatcher) {
        selectedWatcher.type = selectedWatcher.type === "all" ? "state" : "all";
    }

    getPersonName(personId: uuid): string {
        return this.people[personId] || "";
    }
}
